import { useId, type ReactNode } from 'react';

import type { ErrorStateProps } from './types';
import { errorStateStyles } from './styles';

const DEFAULT_TITLE = 'Something went wrong';
const DEFAULT_RETRY_LABEL = 'Try again';

/**
 * A failure the operator should act on.
 *
 * `role="alert"` is an ASSERTIVE live region: a failure interrupts whatever is
 * on screen, which is correct here and is the reason this is a different role
 * from `EmptyState`. An empty list is not an emergency and must never steal
 * focus the way a real failure does.
 *
 * The retry control is a native `<button>` rather than `@/shared/ui/button`.
 * That is not a shortcut around the primitive set — it keeps this state
 * dependency-free so it can render inside any layer, including a global error
 * boundary that itself failed to load.
 *
 * `onRetry` is omitted rather than rendered disabled when there is nothing to
 * retry. A greyed-out "Try again" under a permission failure teaches operators
 * that the button is usually broken.
 */
export function ErrorState({
  title = DEFAULT_TITLE,
  message,
  onRetry,
  retryLabel = DEFAULT_RETRY_LABEL,
}: ErrorStateProps): ReactNode {
  const titleId = useId();

  return (
    <div role="alert" aria-labelledby={titleId} className={errorStateStyles.root}>
      <p id={titleId} className={errorStateStyles.title}>
        {title}
      </p>

      <p className={errorStateStyles.message}>{message}</p>

      {onRetry === undefined ? null : (
        <button type="button" onClick={onRetry} className={errorStateStyles.retry}>
          {retryLabel}
        </button>
      )}
    </div>
  );
}
